import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, CurrencyCircleDollar, ChartBar, Ranking, Globe, Lightning
} from "@phosphor-icons/react";

function formatUsd(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
  return `$${val.toFixed(0)}`;
}

function TvlBar({ tvl, maxTvl }) {
  const pct = maxTvl > 0 ? (tvl / maxTvl) * 100 : 0;
  return (
    <div className="w-full bg-zinc-800/40 rounded-sm h-2 overflow-hidden">
      <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: "rgba(59,130,246,0.5)" }} />
    </div>
  );
}

function ChainRow({ chain, maxTvl, index }) {
  const rankColors = ["text-amber-400", "text-zinc-300", "text-amber-700"];
  const isFeatured = chain.featured;

  return (
    <div
      data-testid={`chain-row-${chain.rank}`}
      className={`px-4 py-3 border-b transition-colors group ${
        isFeatured
          ? "border-l-2 border-l-emerald-500/60 border-b-zinc-800/40 bg-emerald-950/10 hover:bg-emerald-950/20"
          : "border-zinc-800/40 hover:bg-zinc-900/40"
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Rank */}
        <span className={`text-sm font-data font-bold w-8 text-center flex-shrink-0 ${index < 3 ? rankColors[index] : "text-zinc-600"}`}>
          {chain.rank}
        </span>

        {/* Name + Symbol */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${chain.color}18` }}>
            <span className="text-[10px] font-data font-bold" style={{ color: chain.color }}>
              {chain.symbol.slice(0, 3)}
            </span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-zinc-100">{chain.name}</p>
              {isFeatured && (
                <Badge className="text-[7px] px-1.5 py-0 border-0 rounded-sm bg-emerald-900/40 text-emerald-400 h-3.5">
                  TRACKED
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-zinc-500 font-data">{chain.symbol}</span>
              <Badge className="text-[7px] px-1 py-0 border-0 rounded-sm bg-zinc-800/60 text-zinc-500 h-3.5">
                {chain.type}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-2 ml-11 flex-wrap">
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">TVL</p>
          <p className="text-xs font-data font-medium text-zinc-100">{formatUsd(chain.tvl)}</p>
          <TvlBar tvl={chain.tvl} maxTvl={maxTvl} />
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">24h DEX Vol</p>
          <p className={`text-xs font-data font-medium ${chain.dex_volume_24h > 0 ? "text-zinc-200" : "text-zinc-600"}`}>
            {chain.dex_volume_24h > 0 ? formatUsd(chain.dex_volume_24h) : "--"}
          </p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Vol/TVL</p>
          {chain.tvl > 0 && chain.dex_volume_24h > 0 ? (
            <p className={`text-xs font-data font-medium ${
              chain.dex_volume_24h / chain.tvl > 0.1 ? "text-emerald-400" : chain.dex_volume_24h / chain.tvl > 0.03 ? "text-zinc-300" : "text-zinc-500"
            }`}>
              {((chain.dex_volume_24h / chain.tvl) * 100).toFixed(1)}%
            </p>
          ) : (
            <span className="text-xs text-zinc-600 font-data">--</span>
          )}
        </div>
        <p className="text-[8px] text-zinc-600">Vol/TVL</p>
      </div>
    </div>
  );
}

export default function Blockchains() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchChains = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/blockchains`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to fetch blockchains:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchChains(); }, []);

  const chains = data?.chains || [];
  const maxTvl = chains.length > 0 ? Math.max(...chains.map(c => c.tvl)) : 1;
  // Separate: top ranked chains + featured chains that may be outside top 10
  const topRanked = chains.filter(c => c.rank <= 10);
  const featuredOutside = chains.filter(c => c.rank > 10 && c.featured);
  const top10 = [...topRanked, ...featuredOutside];

  return (
    <div className="p-5 max-w-[1200px]" data-testid="blockchains-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Globe size={22} weight="bold" className="text-blue-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Top Blockchains</h1>
          </div>
          <p className="text-xs text-zinc-500 ml-[34px]">
            Live TVL and DEX volume from DeFiLlama
          </p>
        </div>
        <button
          data-testid="refresh-chains-btn"
          onClick={() => fetchChains(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm"
        >
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Updating..." : "Refresh"}
        </button>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="chain-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <CurrencyCircleDollar size={14} className="text-blue-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Total TVL</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{formatUsd(data.total_tvl)}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Ranking size={14} className="text-amber-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">#1 Chain</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{top10[0]?.name || "--"}</p>
            <p className="text-[10px] text-zinc-500">{formatUsd(top10[0]?.tvl)}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <ChartBar size={14} className="text-emerald-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Total DEX Vol (Top 10)</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">
              {formatUsd(top10.reduce((s, c) => s + (c.dex_volume_24h || 0), 0))}
            </p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Lightning size={14} className="text-violet-400" />
              <span className="text-[9px] uppercase tracking-wider text-zinc-500">Tracked Chains</span>
            </div>
            <p className="text-lg font-data font-bold text-zinc-100">{chains.length}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-14 bg-zinc-900/50" />)}
        </div>
      )}

      {/* Table */}
      {!loading && top10.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="chains-table">
          {/* Header */}
          <div className="grid grid-cols-[40px_1fr_130px_130px_100px] items-center gap-3 px-4 py-2.5 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600">Blockchain</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">TVL</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">DEX Volume</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-600 text-right">Vol/TVL</span>
          </div>

          {/* Rows */}
          {top10.map((chain, i) => (
            <ChainRow key={chain.name} chain={chain} maxTvl={maxTvl} index={i} />
          ))}
        </div>
      )}

      {/* Remaining chains (outside top 10, not featured) as compact list */}
      {!loading && chains.filter(c => c.rank > 10 && !c.featured).length > 0 && (
        <div className="mt-4" data-testid="chains-overflow">
          <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-600 mb-2 font-medium">Also Tracked</p>
          <div className="flex flex-wrap gap-2">
            {chains.filter(c => c.rank > 10 && !c.featured).map(chain => (
              <div key={chain.name} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-900/30 border border-zinc-800/40 rounded-sm">
                <span className="text-[9px] font-data text-zinc-500">{chain.rank}.</span>
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: chain.color }} />
                <span className="text-[10px] text-zinc-300">{chain.name}</span>
                <span className="text-[9px] font-data text-zinc-500">{formatUsd(chain.tvl)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      {data && (
        <div className="mt-4 pt-3 border-t border-zinc-800/40 flex items-center justify-between">
          <p className="text-[10px] text-zinc-600">Source: DeFiLlama (defillama.com)</p>
          <p className="text-[10px] text-zinc-600">
            Updated: {new Date(data.fetched_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
